// @ts-nocheck
import type {
  CartDiscountsV2PromotionTypeEnum,
  SellerPromotionV2,
} from '@redteclab/api/clients/bully'
import classNames from 'clsx'
import { type FC } from 'react'

import { DOCUMENT_TYPE } from 'shared/consts/checkout'
import { OFFER_PROMOTION_TYPES } from 'shared/consts/marketplace'
import { PRESCRIPTION_TYPE_ERX } from 'shared/consts/productTemplates'
import {
  CART_FREE_GIFT_PROMOTION,
  EXPERIMENT_VARIATION,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import { useGetExperimentNamePerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import {
  CartControllerCartShippingDetailV2,
  CartControllerGroupEntryERx,
  CartControllerGroupEntryV2,
} from 'types/api/cart'
import { PublicRuntimeConfig } from 'types/shopConfig'
import PrescriptionTypeTranslation from 'views/components/atoms/PrescriptionTypeTranslation'
import ProductListing from 'views/components/organisms/Cart/ProductListing'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

interface OdsViewDataConfigType {
  disabledSelect: boolean
  isFreebie: boolean
  showAdditionalInformation: boolean
}

interface ProductTypeListingProps {
  items: CartControllerGroupEntryV2[]
  headline: string
  seller: string
  sellerId: string
  viewData: OdsViewDataConfigType
  currency: string
  isERx?: boolean
  isOverdraft?: boolean
  sortedTypedAmount?: number
  isERxOrOdsInCart?: boolean
  hasOverdrafts?: boolean
  sellerPromotions?: Record<CartDiscountsV2PromotionTypeEnum, string[]>
  sellerPromotion?: SellerPromotionV2
  cartShippingDetail?: CartControllerCartShippingDetailV2
  promotionOfferId?: string
}

const ProductTypeListing: FC<ProductTypeListingProps> = ({
  headline,
  items,
  viewData,
  sellerId,
  sellerPromotions,
  sellerPromotion,
  currency,
  isERx = false,
  sortedTypedAmount = 0,
  isERxOrOdsInCart = false,
  hasOverdrafts = false,
  cartShippingDetail,
}) => {
  const cartFreeGiftPromotionExperiment = useGetExperiment(
    useGetExperimentNamePerTenant(CART_FREE_GIFT_PROMOTION)
  )
  const isCartFreeGiftPromotionExperimentV1 =
    cartFreeGiftPromotionExperiment.isEnabled &&
    cartFreeGiftPromotionExperiment.variant === EXPERIMENT_VARIATION.V1

  if (
    //This is needed to show NO prescription type headline if there are ONLY noPrescription products in the cart
    sortedTypedAmount === 1 &&
    headline === 'noPrescription' &&
    !isERxOrOdsInCart &&
    !hasOverdrafts
  ) {
    return (
      <div className="o-ProductListCart__onlyNoPrescription">
        <ProductListing
          productType={!isERx ? headline : ''} // For Erx Products productType is evaluated inside ProductListing component
          sellerId={sellerId}
          products={items}
          currency={currency}
          promotions={sellerPromotions}
          sellerPromotion={sellerPromotion}
          cartShippingDetail={cartShippingDetail}
          {...viewData}
        />
      </div>
    )
  } else {
    return (
      <>
        {!isCartFreeGiftPromotionExperimentV1 && (
          <>
            <h5
              data-qa-id={`cart-entry-entryType-${headline}`}
              className={'o-ProductListCart__title'}
            >
              <PrescriptionTypeTranslation prescriptionType={headline} />
            </h5>
            <hr className="o-ProductListCart__divider" />
          </>
        )}
        <div
          className={classNames('o-ProductListCart__freebie', {
            'u-margin-large--bottom o-ProductListCart__freebieV1 u-padding--sides':
              isCartFreeGiftPromotionExperimentV1,
          })}
        >
          <ProductListing
            productType={!isERx ? headline : ''} // For Erx Products productType is evaluated inside ProductListing component
            isERx={isERx}
            sellerId={sellerId}
            products={items}
            promotions={sellerPromotions}
            cartShippingDetail={cartShippingDetail}
            {...viewData}
          />
        </div>
      </>
    )
  }
}

interface ProductListCartPerSellerProps {
  productTypes: CartControllerGroupEntryV2[]
  eRxEntries: CartControllerGroupEntryERx[]
  seller: string
  sellerId: string
  sellerGroupId: string
  currency: string
  showStaticShopFreebies?: boolean
  publicRuntimeConfig: PublicRuntimeConfig
  hasOverdrafts: boolean
  sellerPromotions: Record<CartDiscountsV2PromotionTypeEnum, string[]>
  sellerPromotions: SellerPromotionV2
  cartShippingDetail?: CartControllerCartShippingDetailV2
}

const ProductListCartPerSeller: FC<ProductListCartPerSellerProps> = ({
  productTypes,
  eRxEntries,
  sellerId,
  currency,
  sellerGroupId,
  seller,
  sellerPromotions,
  sellerPromotion,
  showStaticShopFreebies = false,
  hasOverdrafts,
  cartShippingDetail,
  publicRuntimeConfig: {
    publicConfig: { staticShopFreebies },
  },
}) => {
  const odsPrescriptionItems =
    productTypes
      .find(x => x.type === DOCUMENT_TYPE.ODS_SECTION)
      ?.items?.filter(x => !x.isFreebie) || []

  const noPrescriptionItems = productTypes
    .find(prescription => prescription.type === DOCUMENT_TYPE.NO_PRESCRIPTION)
    ?.items?.filter(
      item =>
        !item.isFreebie &&
        item.matchedOverdrafts &&
        item.matchedOverdrafts.length <= 0
    )

  const sortedTypes = [
    productTypes.find(x => x.type === DOCUMENT_TYPE.INSURANCE_PRESCRIPTION),
    productTypes.find(x => x.type === DOCUMENT_TYPE.FREE_PRESCRIPTION),
    productTypes.find(x => x.type === DOCUMENT_TYPE.PRIVATE_PRESCRIPTION),
  ].filter(Boolean)

  if (noPrescriptionItems?.length > 0) {
    sortedTypes.unshift({
      type: DOCUMENT_TYPE.NO_PRESCRIPTION,
      items: noPrescriptionItems,
    })
  }

  let freebies = (showStaticShopFreebies && staticShopFreebies) || []

  const freeCartItems = productTypes
    .find(x => x.type === DOCUMENT_TYPE.NO_PRESCRIPTION)
    ?.items?.filter(x => x.isFreebie)

  if (freeCartItems) {
    freebies = freebies.concat(freeCartItems)
  }

  const offerViewDataConfig = {
    cta: false,
    noRemovalCTA: true,
    disabledSelect: false,
    renderBadge: false,
    showRedPoints: true,
    hideRedPoints: true,
    isFreebie: false,
    showAdditionalInformation: false,
  }

  const freebieViewDataConfig = {
    cta: false,
    noRemovalCTA: true,
    disabledSelect: true,
    renderBadge: false,
    showRedPoints: false,
    hideRedPoints: true,
    isFreebie: true,
    showAdditionalInformation: true,
  }

  const eRxViewDataConfig = {
    disabledSelect: true,
    isFreebie: false,
    showAdditionalInformation: false,
  }

  const odsViewDataConfig = {
    disabledSelect: true,
    isFreebie: false,
    showAdditionalInformation: false,
  }

  const erxEntriesWithPrescriptionType = eRxEntries?.map(erxEntry => {
    return {
      ...erxEntry,
      items: erxEntry.items.map(item => {
        return {
          ...item,
          prescriptionType: erxEntry.type,
        }
      }),
    }
  })

  const flattenedErxEntriesWithPrescriptionType =
    erxEntriesWithPrescriptionType?.reduce((accumulator, currentValue) => {
      return accumulator.concat(currentValue.items)
    }, [])

  return (
    <div>
      {odsPrescriptionItems.length > 0 && (
        <ProductTypeListing
          currency={currency}
          headline={DOCUMENT_TYPE.ODS_SECTION}
          items={odsPrescriptionItems}
          seller={seller}
          sellerId={sellerId}
          sellerPromotions={sellerPromotions}
          viewData={odsViewDataConfig}
          cartShippingDetail={cartShippingDetail}
        />
      )}

      {sortedTypes.map(sortedType => (
        <ProductTypeListing
          currency={currency}
          key={`${seller}-${sellerGroupId}-${sortedType.type}`}
          headline={sortedType.type}
          items={sortedType.items}
          seller={seller}
          sellerId={sellerId}
          viewData={offerViewDataConfig}
          sortedTypedAmount={sortedTypes.length}
          isERxOrOdsInCart={
            !!flattenedErxEntriesWithPrescriptionType?.length ||
            !!odsPrescriptionItems?.length
          }
          hasOverdrafts={hasOverdrafts}
          sellerPromotions={sellerPromotions}
          sellerPromotion={sellerPromotion}
          cartShippingDetail={cartShippingDetail}
        />
      ))}

      {flattenedErxEntriesWithPrescriptionType?.length > 0 && (
        <ProductTypeListing
          currency={currency}
          headline={PRESCRIPTION_TYPE_ERX}
          items={flattenedErxEntriesWithPrescriptionType}
          seller={seller}
          sellerId={sellerId}
          viewData={eRxViewDataConfig}
          cartShippingDetail={cartShippingDetail}
          isERx
        />
      )}

      {freebies.length > 0 && (
        <ProductTypeListing
          currency={currency}
          headline={OFFER_PROMOTION_TYPES.FREEBIE}
          items={freebies}
          seller={seller}
          sellerId={sellerId}
          viewData={freebieViewDataConfig}
          cartShippingDetail={cartShippingDetail}
        />
      )}
    </div>
  )
}

export default withShopAndRuntimeConfig(ProductListCartPerSeller)
